import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight } from "lucide-react";
import { API } from "../constants";
import { Reveal } from "./motion";

const interests = ["Soul in Love", "Amber Ellis", "Tundala", "Tonal Intentions", "General"];

const ContactForm = ({ defaultInterest = "General", dark = false }) => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    organization: "",
    interest: defaultInterest,
    message: "",
  });
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/contact`, {
        ...form,
        organization: form.organization || null,
      });
      toast.success("Your message has been sent. Amber will be in touch.");
      setForm({ name: "", email: "", organization: "", interest: defaultInterest, message: "" });
    } catch (err) {
      toast.error("Something went wrong sending your message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const labelCls = `mb-2 block text-[0.6rem] font-light uppercase tracking-[0.3em] ${
    dark ? "text-white/50" : "text-black/50"
  }`;
  const fieldCls = dark ? "field field-dark" : "field";

  return (
    <Reveal>
      <form
        data-testid="contact-form"
        onSubmit={submit}
        className={`mx-auto w-full max-w-3xl border p-8 text-left backdrop-blur-sm md:p-12 ${
          dark ? "hairline-light bg-white/[0.03]" : "hairline bg-white/70 shadow-[0_20px_60px_rgba(22,19,14,0.06)]"
        }`}
      >
        <p className={`mb-2 text-[0.65rem] font-light uppercase tracking-[0.45em] ${dark ? "text-[#d4af37]" : "text-[#8a6d1f]"}`}>
          Begin the Conversation
        </p>
        <h3 className={`font-serif-display mb-10 text-2xl font-light md:text-3xl ${dark ? "text-white" : "text-[#16130e]"}`}>
          Every collaboration begins with a single note.
        </h3>

        <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
          <div>
            <label htmlFor="cf-name" className={labelCls}>Name</label>
            <input
              id="cf-name"
              data-testid="contact-name-input"
              required
              value={form.name}
              onChange={set("name")}
              className={fieldCls}
              placeholder="Your name"
            />
          </div>
          <div>
            <label htmlFor="cf-email" className={labelCls}>Email</label>
            <input
              id="cf-email"
              data-testid="contact-email-input"
              type="email"
              required
              value={form.email}
              onChange={set("email")}
              className={fieldCls}
              placeholder="you@example.com"
            />
          </div>
          <div>
            <label htmlFor="cf-org" className={labelCls}>
              Organization <span className={dark ? "text-white/25" : "text-black/25"}>(optional)</span>
            </label>
            <input
              id="cf-org"
              data-testid="contact-organization-input"
              value={form.organization}
              onChange={set("organization")}
              className={fieldCls}
              placeholder="Company or team"
            />
          </div>
          <div>
            <label htmlFor="cf-interest" className={labelCls}>I'm drawn to</label>
            <select
              id="cf-interest"
              data-testid="contact-interest-select"
              value={form.interest}
              onChange={set("interest")}
              className={fieldCls}
            >
              {interests.map((i) => (
                <option key={i} value={i} className="bg-[#0d0d0b] text-white">
                  {i}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-8">
          <label htmlFor="cf-message" className={labelCls}>Message</label>
          <textarea
            id="cf-message"
            data-testid="contact-message-input"
            required
            rows={5}
            value={form.message}
            onChange={set("message")}
            className={`${fieldCls} resize-none`}
            placeholder="What are you hoping to create, shift, or explore?"
          />
        </div>

        <button
          data-testid="contact-submit-button"
          type="submit"
          disabled={sending}
          className="btn-solid mt-10 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {sending ? "Sending…" : "Send Message"}
          {!sending && <ArrowUpRight size={13} strokeWidth={1.5} />}
        </button>
      </form>
    </Reveal>
  );
};

export default ContactForm;
